// 스킬 연출 표시 계층(COMBAT-FEEL 9절, 사이클 20261019-skill-vfx, 설계도 c-storm-a·c-dash).
// - StormRange: 칼날 폭풍의 판정 반경(5m)을 가리키는 얇은 바닥 고리. 시전~종료 동안만, 취소되면 그 틱에 지운다.
// - DashCrack: 피의 돌진이 실제로 지나간 경로(영웅 렌더 위치 표본)를 따라 그리는 좁은 바닥 균열과 판정 폭(±반폭) 점선.
//   고정 7m가 아니라 실제 이동만큼 자라므로 벽에 막히면 그 자리에서 멈춘다. 깊이 검사를 켜 벽·기둥 뒤에는 보이지 않는다.
// 표시 계층만: 판정·수치는 게임(src/game)이 정하고 여기서는 읽지 않는다. 프레임 루프에서 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { abs, attribute, float, fract, mix, sin, smoothstep, step, uniform, vec3 } from 'three/tsl';

/** 고리 바닥 높이(m): 경고 데칼보다 아래 순서로 그려 경고선을 덮지 않는다 */
const RING_Y = 0.03;

export class StormRange {
  readonly group = new THREE.Group();
  private readonly line: THREE.Mesh;
  private readonly band: THREE.Mesh;
  private readonly lineOp = uniform(0);
  private readonly bandOp = uniform(0);
  private start = -1e9;
  private end = -1e9;
  private on = false;
  /** 끊긴(취소) 시각: 이 뒤로는 즉시 꺼진다 */
  private cutAt = -1;

  constructor() {
    // 단위 반경 고리 두 장: 크림 선(바깥 0.985~1.0) + 바로 안쪽 옅은 진홍 띠(0.955~0.985, 밝은 돌바닥에서 대비)
    const lg = new THREE.RingGeometry(0.984, 1.0, 128, 1);
    lg.rotateX(-Math.PI / 2);
    const bg = new THREE.RingGeometry(0.95, 0.984, 128, 1);
    bg.rotateX(-Math.PI / 2);
    const lm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    lm.fog = false;
    lm.colorNode = vec3(1.0, 0.9, 0.74);
    lm.opacityNode = this.lineOp;
    const bm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    bm.fog = false;
    bm.colorNode = vec3(0.62, 0.06, 0.1);
    bm.opacityNode = this.bandOp;
    this.line = new THREE.Mesh(lg, lm);
    this.band = new THREE.Mesh(bg, bm);
    for (const m of [this.line, this.band]) { m.renderOrder = 1; m.frustumCulled = false; this.group.add(m); }
    this.group.visible = false;
  }

  /** now: 영웅 시계(틱), r: 판정 반경(m), ticks: 스킬 길이(틱) */
  begin(now: number, x: number, y: number, z: number, r: number, ticks: number): void {
    this.start = now; this.end = now + ticks; this.on = true; this.cutAt = -1;
    this.group.position.set(x, y + RING_Y, z);
    this.group.scale.set(r, 1, r);
    this.group.visible = true;
  }

  /** 스킬이 끝나거나 끊김: cut이면 바로 지우고, 아니면 6틱에 걸쳐 사라진다 */
  stop(now: number, cut: boolean): void {
    if (!this.on) return;
    if (cut) { this.cutAt = now; this.on = false; this.group.visible = false; this.lineOp.value = 0; this.bandOp.value = 0; return; }
    this.end = Math.min(this.end, now);
  }

  /** 영웅이 밀리면 고리도 따라간다(판정은 매 회차 영웅 위치 기준) */
  update(now: number, x: number, y: number, z: number): void {
    if (!this.on) return;
    const t = Math.max(0, now - this.start);
    const fadeIn = Math.min(1, t / 3);
    const after = now - this.end;
    const fadeOut = after <= 0 ? 1 : Math.max(0, 1 - after / 6);
    if (fadeOut <= 0) { this.on = false; this.group.visible = false; return; }
    const k = fadeIn * fadeOut;
    this.lineOp.value = 0.78 * k;
    this.bandOp.value = 0.32 * k;
    this.group.position.set(x, y + RING_Y, z);
  }

  get visible(): boolean { return this.on && this.group.visible; }
  get wasCut(): boolean { return this.cutAt >= 0; }

  /** 미리 컴파일: 장면 준비 때 한 프레임 보이게 두어 첫 시전에서 셰이더 컴파일로 끊기지 않게 한다 */
  prewarm(on: boolean): void { this.group.visible = on || this.on; }

  clear(): void { this.on = false; this.group.visible = false; }

  dispose(): void {
    for (const m of [this.line, this.band]) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
  }
}

/** 균열 표본 수(링 버퍼 아님: 한 번의 돌진 = 최대 N표본) */
const N = 72;
/** 표본 최소 간격(m) */
const MIN_STEP = 0.12;
/** 균열 반폭(m)·들쭉날쭉 폭 */
const CRACK_HW = 0.55;
/** 점선 반폭(m) */
const EDGE_HW = 0.045;
/** 돌진이 끝난 뒤 사라지는 시간(틱) */
const FADE_TICKS = 36;
/** 점선이 사라지는 시간(틱): 판정 폭 표시는 짧게 */
const EDGE_FADE_TICKS = 20;
/** 성장 안전 상한(틱): 돌진 25틱 + 여유 */
const MAX_GROW_TICKS = 34;

export class DashCrack {
  readonly mesh: THREE.Mesh;
  private readonly pos: Float32Array;
  private readonly sAttr: Float32Array;
  private readonly sx = new Float32Array(N);
  private readonly sz = new Float32Array(N);
  private readonly ss = new Float32Array(N);
  private count = 0;
  private start = -1e9;
  private endAt = -1e9;
  private y = 0;
  private growing = false;
  private shown = false;
  /** 판정 반폭(m): 점선을 이 거리에 그린다 */
  private half = 1.8;
  /** 0..1: 갈라진 틈 속 진홍 빛 */
  private readonly glow = uniform(1);
  /** 0..1: 균열 전체 불투명도 */
  private readonly alpha = uniform(1);
  /** 0..1: 점선 불투명도 */
  private readonly edge = uniform(1);
  /** 섬광 약화: 빛 배율 */
  readonly gain = uniform(1);

  constructor() {
    // 띠 3개(균열·왼쪽 점선·오른쪽 점선) × 표본 N × 정점 2
    const V = 3 * N * 2;
    this.pos = new Float32Array(V * 3);
    this.sAttr = new Float32Array(V);
    const vv = new Float32Array(V), band = new Float32Array(V);
    const idx: number[] = [];
    for (let b = 0; b < 3; b++) {
      for (let i = 0; i < N; i++) {
        const o = (b * N + i) * 2;
        vv[o] = 0; vv[o + 1] = 1;
        band[o] = band[o + 1] = b === 0 ? 0 : 1;
        if (i < N - 1) { const a = o, c = o + 2; idx.push(a, c, a + 1, a + 1, c, c + 1); }
      }
    }
    const g = new THREE.BufferGeometry();
    const pa = new THREE.BufferAttribute(this.pos, 3); pa.setUsage(THREE.DynamicDrawUsage);
    const sa = new THREE.BufferAttribute(this.sAttr, 1); sa.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', pa);
    g.setAttribute('aS', sa);
    g.setAttribute('aV', new THREE.BufferAttribute(vv, 1));
    g.setAttribute('aBand', new THREE.BufferAttribute(band, 1));
    g.setIndex(idx);
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    // 일반 투명 혼합 + 깊이 검사: 밝은 돌바닥에서도 어두운 갈라짐이 보이고, 벽·기둥 뒤로는 그려지지 않는다
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
    m.fog = false;
    const s = attribute('aS', 'float'), v = attribute('aV', 'float'), bnd = attribute('aBand', 'float');
    const across = abs(v.mul(2).sub(1)); // 0 가운데 → 1 가장자리
    // 균열: 가운데 좁은 진홍 심(빛) + 어두운 갈라짐 + 바깥으로 갈수록 투명
    // 빛나는 심은 가운데 35%, 어두운 갈라짐은 그 바깥, 가장자리 20%는 투명으로 번진다(c-dash: 진홍 틈 + 검은 갈라진 돌)
    // smoothstep은 low<high 순서로만 쓰고 1-에서 뒤집는다(뒤집힌 경계는 백엔드마다 결과가 다르다)
    // 2026-10-19 정본 근접 캡처: 가장자리가 반듯한 어두운 띠는 옆에서 보면 판자처럼 보였다 → 경로 길이 s에 따른 고정 잡음으로 가장자리를 들쭉날쭉하게
    const jag = sin(s.mul(9.0)).mul(0.5).add(sin(s.mul(23.0).add(1.7)).mul(0.3)).add(sin(s.mul(41.0).add(0.4)).mul(0.2));
    const edgeAt = jag.mul(0.16).add(0.78);
    const core = float(1).sub(smoothstep(float(0.04), jag.mul(0.05).add(0.2), across));
    const crackA = float(1).sub(smoothstep(edgeAt.sub(0.05), edgeAt, across)).mul(0.82);
    const dark = vec3(0.05, 0.025, 0.02);
    const hot = vec3(1.9, 0.1, 0.08).mul(this.gain);
    const crackC = mix(dark, hot, core.mul(this.glow));
    // 점선: 0.7m 주기의 55%만 그린다(판정 폭 경계), 크림색
    const dash = step(fract(s.div(0.7)), float(0.55));
    const edgeA = dash.mul(float(1).sub(smoothstep(float(0.3), float(1.0), across))).mul(0.55).mul(this.edge);
    m.colorNode = mix(crackC, vec3(1.0, 0.9, 0.72), bnd);
    m.opacityNode = mix(crackA.mul(this.alpha), edgeA, bnd);
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    this.mesh.visible = false;
  }

  /** Ev.SkillCast(돌진) 순간: now 영웅 시계(틱), (x,z) 영웅 렌더 위치, facing 방향, half 판정 반폭 */
  begin(now: number, x: number, y: number, z: number, facing: number, half: number): void {
    this.start = now; this.y = y; this.half = half;
    this.growing = true; this.shown = true; this.count = 0;
    this.glow.value = 1; this.alpha.value = 1; this.edge.value = 1;
    // 출발점 조금 뒤에서 시작(c-dash: 균열이 영웅 뒤에서 시작해 발밑에서 끝난다)
    this.push(x - Math.sin(facing) * 0.35, z - Math.cos(facing) * 0.35);
    this.push(x, z);
    this.rebuild();
  }

  private push(x: number, z: number): void {
    if (this.count >= N) return;
    const i = this.count;
    this.sx[i] = x; this.sz[i] = z;
    this.ss[i] = i === 0 ? 0 : this.ss[i - 1] + Math.hypot(x - this.sx[i - 1], z - this.sz[i - 1]);
    this.count++;
  }

  /**
   * 매 렌더 프레임. now 영웅 시계, (x,z) 영웅 렌더 위치, dashing: 게임 영웅이 아직 돌진 스킬 중인가.
   * 돌진이 끝났거나 끊긴 첫 프레임에 true를 돌려준다(끝점 = 그 프레임의 실제 위치).
   */
  update(now: number, x: number, y: number, z: number, dashing: boolean): boolean {
    if (!this.shown) return false;
    let ended = false;
    if (this.growing) {
      const t = now - this.start;
      const last = this.count - 1;
      if (Math.hypot(x - this.sx[last], z - this.sz[last]) >= MIN_STEP) this.push(x, z);
      if ((!dashing && t >= 0) || t > MAX_GROW_TICKS) {
        // 마지막 위치를 끝점으로 확정(표본 간격보다 짧아도)
        if (Math.hypot(x - this.sx[this.count - 1], z - this.sz[this.count - 1]) > 0.01) this.push(x, z);
        this.growing = false; this.endAt = now; ended = true;
      }
      this.y = y;
    } else {
      const a = now - this.endAt;
      this.glow.value = Math.max(0, 1 - a / (FADE_TICKS * 0.5));
      this.alpha.value = Math.max(0, 1 - a / FADE_TICKS);
      this.edge.value = Math.max(0, 1 - a / EDGE_FADE_TICKS);
      if (a >= FADE_TICKS) { this.shown = false; this.mesh.visible = false; return false; }
    }
    this.rebuild();
    return ended;
  }

  /** 띠 정점 다시 채우기: 진행 방향의 수직으로 폭을 벌린다. 균열은 표본마다 고정 잡음으로 들쭉날쭉 */
  private rebuild(): void {
    const p = this.pos, sa = this.sAttr, n = this.count;
    for (let b = 0; b < 3; b++) {
      for (let i = 0; i < N; i++) {
        const k = Math.min(i, n - 1);
        const k0 = Math.max(0, k - 1), k1 = Math.min(n - 1, k + 1);
        let dx = this.sx[k1] - this.sx[k0], dz = this.sz[k1] - this.sz[k0];
        const l = Math.hypot(dx, dz) || 1; dx /= l; dz /= l;
        const nx = dz, nz = -dx; // 진행 방향의 오른쪽
        const s = this.ss[k];
        let off = 0, hw = EDGE_HW;
        if (b === 0) {
          // 들쭉날쭉: 표본 거리 s의 고정 함수(매 프레임 같은 모양)
          off = (Math.sin(s * 4.1) * 0.55 + Math.sin(s * 11.3 + 1.3) * 0.45) * 0.16;
          hw = CRACK_HW * (0.55 + 0.45 * Math.abs(Math.sin(s * 2.3 + 0.7))) * Math.min(1, (s + 0.15) / 0.6);
        } else off = b === 1 ? -this.half : this.half;
        const cx = this.sx[k] + nx * off, cz = this.sz[k] + nz * off;
        const o = ((b * N + i) * 2) * 3;
        p[o] = cx - nx * hw; p[o + 1] = this.y + 0.035; p[o + 2] = cz - nz * hw;
        p[o + 3] = cx + nx * hw; p[o + 4] = this.y + 0.035; p[o + 5] = cz + nz * hw;
        const q = (b * N + i) * 2;
        sa[q] = s; sa[q + 1] = s;
      }
    }
    this.mesh.visible = n > 1;
    (this.mesh.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.mesh.geometry.getAttribute('aS') as THREE.BufferAttribute).needsUpdate = true;
  }

  get isGrowing(): boolean { return this.growing; }
  get isShown(): boolean { return this.shown; }

  /** 미리 컴파일(StormRange.prewarm과 같다) */
  prewarm(on: boolean): void { this.mesh.visible = on || (this.shown && this.count > 1); }
  /** 자란 길이(m, 출발점 뒤 0.35m 포함) */
  get length(): number { return this.count > 0 ? this.ss[this.count - 1] : 0; }
  /** 영웅 출발점부터 잰 길이(m) */
  get travelled(): number { return this.count > 1 ? this.ss[this.count - 1] - this.ss[1] : 0; }
  get endX(): number { return this.count > 0 ? this.sx[this.count - 1] : 0; }
  get endZ(): number { return this.count > 0 ? this.sz[this.count - 1] : 0; }

  clear(): void { this.growing = false; this.shown = false; this.mesh.visible = false; this.count = 0; }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

/** 칼날 폭풍 회차 호 수(번갈아 쓴다: 판정 간격 9틱 > 표시 8틱이라 동시에 많아야 2개) */
const ARCS = 2;
const ARC_SEG = 48;

/**
 * 칼날 폭풍 회차 호(COMBAT-FEEL 9.2): 영웅 둘레 낮고 얇은 진홍 호. 일반 투명 혼합이라 밝은 돌바닥에서도 짙은 진홍으로 보이고,
 * 흰빛은 앞 가장자리(그려지는 머리)에만 둔다. 가산 혼합 리본은 밝은 바닥에서 흰 분홍 부채로 바랬다(2026-10-19 픽스처 캡처).
 */
export class StormArcs {
  readonly group = new THREE.Group();
  private readonly meshes: THREE.Mesh[] = [];
  private readonly pos: Float32Array[] = [];
  private readonly head: { value: number }[] = [];
  private readonly tail: { value: number }[] = [];
  private readonly fade: { value: number }[] = [];
  private readonly start = new Float64Array(ARCS);
  private readonly draw = new Float32Array(ARCS);
  private readonly end = new Float32Array(ARCS);
  private readonly on = new Uint8Array(ARCS);
  private next = 0;
  /** 섬광 약화: 흰 머리 세기 */
  readonly white = uniform(1);

  constructor() {
    for (let k = 0; k < ARCS; k++) {
      const g = new THREE.BufferGeometry();
      const p = new Float32Array((ARC_SEG + 1) * 2 * 3);
      const uvs = new Float32Array((ARC_SEG + 1) * 2 * 2);
      for (let i = 0; i <= ARC_SEG; i++) { uvs[i * 4] = i / ARC_SEG; uvs[i * 4 + 1] = 0; uvs[i * 4 + 2] = i / ARC_SEG; uvs[i * 4 + 3] = 1; }
      const idx: number[] = [];
      for (let i = 0; i < ARC_SEG; i++) { const a = i * 2; idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
      const pa = new THREE.BufferAttribute(p, 3); pa.setUsage(THREE.DynamicDrawUsage);
      g.setAttribute('position', pa);
      g.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
      g.setIndex(idx);
      g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
      const head = uniform(0), tail = uniform(0), fade = uniform(0);
      const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide });
      m.fog = false;
      const u = attribute('uv', 'vec2').x, v = attribute('uv', 'vec2').y;
      const across = abs(v.mul(2).sub(1));
      // 띠 단면: 가운데 진하고 가장자리로 부드럽게(바깥 25% 페이드)
      const body = float(1).sub(smoothstep(float(0.6), float(1.0), across));
      const shown = step(u, head).mul(smoothstep(tail.sub(0.25), tail, u));
      // 꼬리 쪽은 옅고 머리 쪽이 진하다(휘두르는 방향이 읽히게)
      const grad = smoothstep(head.sub(0.7), head, u).mul(0.65).add(0.35);
      // 앞 가장자리(머리 쪽 14%)만 흰 심
      const lead = smoothstep(head.sub(0.12), head, u).mul(float(1).sub(smoothstep(float(0.0), float(0.5), across)));
      // 바깥 테두리는 밝은 진홍 선(시안 c-storm-a의 가는 밝은 가장자리)
      const rim = smoothstep(float(0.5), float(0.85), across).mul(0.7);
      const crimson = mix(vec3(0.82, 0.03, 0.07), vec3(1.7, 0.35, 0.33), rim);
      m.colorNode = mix(crimson, vec3(2.0, 1.9, 1.85), lead.mul(this.white));
      m.opacityNode = body.mul(shown).mul(grad).mul(fade).mul(0.95);
      const mesh = new THREE.Mesh(g, m);
      mesh.frustumCulled = false;
      mesh.renderOrder = 20;
      mesh.visible = false;
      this.meshes.push(mesh); this.pos.push(p); this.head.push(head); this.tail.push(tail); this.fade.push(fade);
      this.group.add(mesh);
    }
  }

  /** 호 하나: 중심 (x,y,z), center 방향 기준 ±half를 dir 방향으로 draw틱에 그리고 end틱에 지운다. 쓴 번호를 돌려준다 */
  fire(now: number, x: number, y: number, z: number, center: number, half: number, dir: number, rIn: number, rOut: number, draw: number, end: number): number {
    const k = this.next; this.next = (k + 1) % ARCS;
    const p = this.pos[k];
    const mid = (rIn + rOut) / 2, hw = (rOut - rIn) / 2;
    for (let i = 0; i <= ARC_SEG; i++) {
      const u = i / ARC_SEG;
      const ang = center + dir * (-half + 2 * half * u);
      const taper = Math.pow(Math.sin(Math.PI * Math.min(1, u * 1.15)), 0.5);
      const r0 = mid - hw * taper, r1 = mid + hw * taper;
      const yy = y + 0.18 * (u - 0.5);
      const s = Math.sin(ang), c = Math.cos(ang), o = i * 6;
      p[o] = x + s * r0; p[o + 1] = yy; p[o + 2] = z + c * r0;
      p[o + 3] = x + s * r1; p[o + 4] = yy; p[o + 5] = z + c * r1;
    }
    (this.meshes[k].geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    this.start[k] = now; this.draw[k] = Math.max(1, draw); this.end[k] = end; this.on[k] = 1;
    this.update(now);
    return k;
  }

  update(now: number): void {
    for (let k = 0; k < ARCS; k++) {
      if (!this.on[k]) continue;
      const t = Math.max(0, now - this.start[k]), e = this.end[k], d = this.draw[k];
      if (t >= e) { this.on[k] = 0; this.meshes[k].visible = false; continue; }
      this.meshes[k].visible = true;
      this.head[k].value = Math.min(1, (t + 1) / d);
      const ts = d * 0.6;
      this.tail[k].value = t <= ts ? 0 : Math.min(1.25, ((t - ts) / Math.max(1, e - ts)) * 1.25);
      this.fade[k].value = t <= d ? 1 : Math.max(0, 1 - (t - d) / Math.max(1, e - d));
    }
  }

  /** 지금 보이는 호 수 */
  get active(): number { let n = 0; for (let k = 0; k < ARCS; k++) n += this.on[k]; return n; }

  /** 미리 컴파일(StormRange.prewarm과 같다) */
  prewarm(on: boolean): void { for (let k = 0; k < ARCS; k++) this.meshes[k].visible = on || this.on[k] === 1; }

  /** 스킬 취소: 남은 호를 지금 지운다 */
  cut(): void { for (let k = 0; k < ARCS; k++) { this.on[k] = 0; this.meshes[k].visible = false; } }

  clear(): void { this.cut(); }

  dispose(): void {
    for (const m of this.meshes) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
  }
}


